/**
 * Block 12 PR-D-A Phase D1 Step 1 — quote reducer skeleton.
 *
 * Initial skeleton: только HYDRATE + SET_HYDRATION_STATUS (нужны для Phase D5
 * hydration cascade в Step 9). Domain actions (SET_FURNITURE_TYPE,
 * SET_COST_WITH_MARGIN, COMPOSITION_*, CONDITION_*, CONTACT_*, SET_COMMENTS)
 * добавляются по мере прохода Phases D2-D4.
 *
 * Pattern: useReducer в Container. View — pure presenter, диспатчит через prop.
 */

import type { CompositionField } from './compositionRows';
import { DEFAULT_COMPOSITION_ROWS, DEFAULT_ROW_IDS } from './compositionRows';
import type { ConditionField } from './conditionRows';
import { DEFAULT_CONDITIONS } from './conditionRows';
import type { ContactField } from './contactRows';
import { DEFAULT_CONTACTS, FURNITURE_NAME_CONTACT_ID } from './contactRows';
import type { AnketaSeedPayload } from './state/seedFromAnketa';
import type { ContactRow, QuoteHydrationStatus, QuoteState } from './types';

/**
 * PR-H7 (ADR-0030 §8) — отбрасывает legacy `furniture-name` строку из массива
 * contacts. Используется в HYDRATE для cleanup старых IDB drafts (с 3-contact
 * default до PR-H7). Non-destructive — IDB record остаётся как есть, filter
 * применяется на read time.
 */
function stripLegacyFurnitureNameRow(contacts: ContactRow[] | undefined): ContactRow[] | undefined {
  if (!contacts) return contacts;
  return contacts.filter((row) => row.id !== FURNITURE_NAME_CONTACT_ID);
}

export type QuoteAction =
  /** Phase D5 Step 9 — merge cascade payload (Zustand / IDB / anketa seed). */
  | { type: 'HYDRATE'; payload: Partial<QuoteState> }
  /** Управление hydrationStatus напрямую (skeleton vs ready). */
  | { type: 'SET_HYDRATION_STATUS'; status: QuoteHydrationStatus }
  /**
   * Phase D2 Step 3 — выбор типа мебели (либо из enum, либо custom string).
   * `value=null` reset (закрытие picker без выбора custom не должен
   * дёргать диспатч — обрабатывается на стороне SectionFurnitureType).
   */
  | { type: 'SET_FURNITURE_TYPE'; value: string | null }
  /**
   * Phase D3 Step 4 — заполнить compositionRows из `DEFAULT_COMPOSITION_ROWS`.
   * Идемпотентно: если state.compositionRows.length > 0 → no-op (не затирает
   * Phase D5 anketa-seed либо user-edits).
   */
  | { type: 'COMPOSITION_INIT_DEFAULTS' }
  /** Phase D3 — обновить одну ячейку строки по `id`. */
  | { type: 'COMPOSITION_UPDATE'; id: string; field: CompositionField; value: string }
  /**
   * Phase D3 — добавить custom-строку. Caller передаёт `id` (обычно
   * `genUuid()` из `@/lib/uuid`) — reducer pure, не дёргает crypto-source сам.
   */
  | { type: 'COMPOSITION_ADD_CUSTOM'; id: string }
  /** Phase D3 — удалить строку (default либо custom) по `id`. */
  | { type: 'COMPOSITION_DELETE'; id: string }
  /**
   * Phase D4 Step 5 — стоимость с маржой (₸).
   * `value=null` для пустого input'а; positive number для финального значения.
   * Validation (`cost > 0`) — на стороне Save handler (Phase D5 Step 11).
   */
  | { type: 'SET_COST_WITH_MARGIN'; value: number | null }
  /**
   * PR-D2 (ADR-0040) — хранимая ставка «Аванс, %» (0–100). `value=null` →
   * ручной режим (сумма аванса берётся из строки-условия `advance`). Зеркало
   * SET_COST_WITH_MARGIN: UI парсит '' → null, иначе Number.
   */
  | { type: 'SET_ADVANCE_PERCENT'; value: number | null }
  /** Phase D4 Step 6 — заполнить conditions из DEFAULT_CONDITIONS (idempotent). */
  | { type: 'CONDITION_INIT_DEFAULTS' }
  /** Phase D4 — обновить ячейку строки conditions. */
  | { type: 'CONDITION_UPDATE'; id: string; field: ConditionField; value: string }
  /** Phase D4 — добавить custom condition (caller-supplied UUID). */
  | { type: 'CONDITION_ADD_CUSTOM'; id: string }
  /** Phase D4 — удалить condition (default либо custom). */
  | { type: 'CONDITION_DELETE'; id: string }
  /** Phase D4 Step 6 — заполнить executorContacts из DEFAULT_CONTACTS (idempotent). */
  | { type: 'CONTACT_INIT_DEFAULTS' }
  /** Phase D4 — обновить ячейку строки contacts. */
  | { type: 'CONTACT_UPDATE'; id: string; field: ContactField; value: string }
  /** Phase D4 — добавить custom contact (caller-supplied UUID). */
  | { type: 'CONTACT_ADD_CUSTOM'; id: string }
  /** Phase D4 — удалить contact (default либо custom). */
  | { type: 'CONTACT_DELETE'; id: string }
  /** Phase D4 Step 7 — свободный комментарий (textarea, любая длина). */
  | { type: 'SET_COMMENTS'; value: string }
  /**
   * PR-H4 — название компании / имя исполнителя (e.g. «ЛучшаяМебель»).
   * Свободная строка любой длины (validation на стороне UI/Save если потребуется).
   */
  | { type: 'SET_COMPANY_NAME'; value: string }
  /**
   * PR-H2 ADR-0027 + PR-H7 ADR-0030 — partial re-seed после Priority 2.6
   * invalidation check. Overwrites: compositionRows (default rows + preserved
   * custom UUID rows), costWithMargin, furnitureType, seededAt +
   * lastAnketaUpdatedAt. Preserves: conditions, comments, companyName,
   * executorContacts (Q1=B + PR-H7: furniture-name row больше не существует,
   * поэтому нет mutation сюда).
   */
  | { type: 'PARTIAL_RESEED'; payload: AnketaSeedPayload };

export function quoteReducer(state: QuoteState, action: QuoteAction): QuoteState {
  switch (action.type) {
    case 'HYDRATE': {
      // PR-H7 (ADR-0030 §8): отфильтровать legacy `furniture-name` строку
      // из payload (если приходит из старого IDB draft с 3-contact array).
      // Defensive: payload.executorContacts может быть undefined (e.g. when
      // hydrating только furnitureType+cost) — passthrough в этом случае.
      const filteredContacts = stripLegacyFurnitureNameRow(action.payload.executorContacts);
      return {
        ...state,
        ...action.payload,
        ...(filteredContacts ? { executorContacts: filteredContacts } : {}),
        hydrationStatus: 'ready',
      };
    }
    case 'SET_HYDRATION_STATUS':
      return { ...state, hydrationStatus: action.status };
    case 'SET_FURNITURE_TYPE':
      return { ...state, furnitureType: action.value };
    case 'COMPOSITION_INIT_DEFAULTS':
      if (state.compositionRows.length > 0) return state;
      return { ...state, compositionRows: DEFAULT_COMPOSITION_ROWS.map((row) => ({ ...row })) };
    case 'COMPOSITION_UPDATE':
      return {
        ...state,
        compositionRows: state.compositionRows.map((row) =>
          row.id === action.id ? { ...row, [action.field]: action.value } : row,
        ),
      };
    case 'COMPOSITION_ADD_CUSTOM':
      return {
        ...state,
        compositionRows: [
          ...state.compositionRows,
          { id: action.id, name: '', itemType: '', brand: '' },
        ],
      };
    case 'COMPOSITION_DELETE':
      return {
        ...state,
        compositionRows: state.compositionRows.filter((row) => row.id !== action.id),
      };
    case 'SET_COST_WITH_MARGIN':
      return { ...state, costWithMargin: action.value };
    case 'SET_ADVANCE_PERCENT':
      return { ...state, advancePercent: action.value };
    case 'CONDITION_INIT_DEFAULTS':
      if (state.conditions.length > 0) return state;
      return { ...state, conditions: DEFAULT_CONDITIONS.map((row) => ({ ...row })) };
    case 'CONDITION_UPDATE':
      return {
        ...state,
        conditions: state.conditions.map((row) =>
          row.id === action.id ? { ...row, [action.field]: action.value } : row,
        ),
      };
    case 'CONDITION_ADD_CUSTOM':
      return {
        ...state,
        conditions: [...state.conditions, { id: action.id, label: '', value: '', unit: undefined }],
      };
    case 'CONDITION_DELETE':
      return {
        ...state,
        conditions: state.conditions.filter((row) => row.id !== action.id),
      };
    case 'CONTACT_INIT_DEFAULTS':
      if (state.executorContacts.length > 0) return state;
      return { ...state, executorContacts: DEFAULT_CONTACTS.map((row) => ({ ...row })) };
    case 'CONTACT_UPDATE':
      return {
        ...state,
        executorContacts: state.executorContacts.map((row) =>
          row.id === action.id ? { ...row, [action.field]: action.value } : row,
        ),
      };
    case 'CONTACT_ADD_CUSTOM':
      return {
        ...state,
        executorContacts: [...state.executorContacts, { id: action.id, label: '', value: '' }],
      };
    case 'CONTACT_DELETE':
      return {
        ...state,
        executorContacts: state.executorContacts.filter((row) => row.id !== action.id),
      };
    case 'SET_COMMENTS':
      return { ...state, comments: action.value };
    case 'SET_COMPANY_NAME':
      return { ...state, companyName: action.value };
    case 'PARTIAL_RESEED': {
      const incoming = action.payload;

      // Composition: новые 17 default rows из incoming, custom UUID rows preserve.
      const incomingDefaults = incoming.compositionRows ?? [];
      const existingCustom = state.compositionRows.filter((r) => !DEFAULT_ROW_IDS.has(r.id));
      const mergedComposition = [...incomingDefaults, ...existingCustom];

      // PR-H7 (ADR-0030 §8): executorContacts preserved as-is. До PR-H7 эта
      // ветка обновляла value `furniture-name` row из incoming. После ADR-0030
      // строка удалена из DEFAULT_CONTACTS, seedFromAnketa больше не пишет её
      // в incoming, поэтому никаких mutation в contacts здесь не нужно.
      // Defensive stripping legacy furniture-name row из обоих branches —
      // HYDRATE является primary funnel, но PARTIAL_RESEED тоже cleanup'ит
      // на случай если state.executorContacts не прошёл через HYDRATE.
      const mergedContacts =
        state.executorContacts.length === 0
          ? (stripLegacyFurnitureNameRow(incoming.executorContacts) ?? [])
          : (stripLegacyFurnitureNameRow(state.executorContacts) ?? state.executorContacts);

      return {
        ...state,
        projectId: incoming.projectId ?? state.projectId,
        furnitureType: incoming.furnitureType ?? state.furnitureType,
        compositionRows: mergedComposition,
        costWithMargin: incoming.costWithMargin ?? state.costWithMargin,
        executorContacts: mergedContacts,
        seededAt: incoming.seededAt,
        lastAnketaUpdatedAt: incoming.lastAnketaUpdatedAt,
        companyName: state.companyName, // PR-H4 — explicit preserve user-edited value
        // conditions / comments / hydrationStatus — untouched.
      };
    }
    default: {
      assertNever(action);
      return state;
    }
  }
}

function assertNever(value: never): never {
  throw new Error(`quoteReducer: unhandled action ${JSON.stringify(value)}`);
}
